import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import { Loader } from '@/components/custom/loader';
import { TypewriterText } from '@/components/custom/typewriter-text';
import tiaExercicios from '@/assets/images/tia/TIA-EXERCICIOS.png';

// Resultado do aluno num caderno de exercícios: a T.I.A comenta (via OpenAI, no
// backend) + números (aproveitamento, acertos/erros, breakdown por tema).
// Busca GET question_books/:id/result (resposta plana, por isso axios e não jsonapi-react).
export function ExercisesResultSheet({ bookId }) {
  const [state, setState] = useState({ status: 'loading', data: null });

  const load = useCallback(() => {
    setState({ status: 'loading', data: null });
    // O interceptor de resposta do `api` já retorna o corpo (response.data),
    // então o valor resolvido é o próprio payload do resultado.
    api
      .get(`question_books/${bookId}/result`)
      .then(data => setState({ status: 'ready', data }))
      .catch(() => setState({ status: 'error', data: null }));
  }, [bookId]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === 'loading') {
    return (
      <div className="grid place-items-center py-16">
        <Loader />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-sm font-semibold text-neutral-500">Não foi possível carregar seu resultado.</p>
        <button
          type="button"
          onClick={load}
          className="rounded-full border-2 border-neutral-lighter px-4 py-2 text-sm font-bold text-neutral-dark transition-colors hover:border-[var(--color-mission)]"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  const { aproveitamento = 0, acertos = 0, erros = 0, taxonomias = [], comentario } = state.data || {};

  return (
    <div>
      <div className="mb-6 flex items-end gap-3">
        <img
          src={tiaExercicios}
          alt=""
          aria-hidden="true"
          className="pointer-events-none h-24 w-auto shrink-0 self-end object-contain object-bottom"
        />
        <div className="relative z-10 mb-2 flex-1 rounded-2xl rounded-bl-none bg-white p-4 shadow-card">
          <span
            className="block text-xs font-extrabold"
            style={{ color: 'var(--color-secondary-dark)' }}
          >
            T.I.A
          </span>
          <p className="m-0 text-sm font-bold leading-relaxed text-neutral-dark">
            <TypewriterText text={comentario} />
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-100 bg-white p-4 shadow-card">
        <span className="block text-xs font-bold uppercase tracking-wide text-neutral-400">Aproveitamento</span>
        <div className="mt-1 flex items-center gap-3">
          <span
            className="text-3xl font-extrabold"
            style={{ color: '#222731' }}
          >
            {aproveitamento}%
          </span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full"
              style={{ width: `${aproveitamento}%`, background: 'var(--gradient-btn-continue)' }}
            />
          </div>
        </div>
        <div className="mt-3 flex gap-4 text-sm font-bold text-neutral-dark">
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: '#1CA388' }}
            />
            {acertos} acertos
          </span>
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: '#FD6B62' }}
            />
            {erros} erros
          </span>
        </div>
      </div>

      {taxonomias.length > 0 && (
        <div className="mt-4">
          <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-neutral-400">Por tema</span>
          <div className="space-y-2">
            {taxonomias.map(tax => (
              <div
                key={tax.nome}
                className="rounded-xl border border-neutral-100 bg-white p-3"
              >
                <div className="flex items-center justify-between text-sm font-bold text-neutral-dark">
                  <span>{tax.nome}</span>
                  <span>
                    {tax.acertos}/{tax.total}
                  </span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${tax.aproveitamento}%`, background: tax.aproveitamento >= 50 ? '#1CA388' : '#FD6B62' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link
        to={`/questions/apostilas/${bookId}`}
        className="group/btn relative mt-6 block w-full overflow-hidden rounded-full py-3 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
      >
        Responder de novo do início
      </Link>
    </div>
  );
}
